import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Tab, Tabs } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/common/DataTable";
import TaskIconButton from "../../components/common/TaskIconButton";
import Title from "../../components/common/Title";
import { ValidationAlert } from "../../components/common/ValidationAlert";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import useAlert from "../../hooks/useAlert";
import usePersistedState from "../../hooks/usePersistedState";
import ConfirmDeleteDialog from "./components/ConfirmDeleteDialog";
import { CLUSTERS, createTableColumns } from "./constants/taskConstants";
import useTasks from "./hooks/useTasks";

/**
 * Component for managing tasks.
 *
 * @returns {JSX.Element} - The JSX element representing the tasks management interface.
 */
function Tasks() {
  const { tasks, refreshTasks } = useTasks();
  const [cluster, setCluster] = usePersistedState("tasks.cluster", CLUSTERS.SCRAPER, Object.values(CLUSTERS));
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    taskData: null,
  });
  const { alertState, showAlert, closeAlert } = useAlert();
  const navigate = useNavigate();

  // Load tasks when cluster changes
  useEffect(() => {
    refreshTasks(cluster);
  }, [cluster]);

  const openDeleteDialog = (taskData) => {
    setDeleteDialog({
      isOpen: true,
      taskData: taskData,
    });
  };

  // Handlers
  const handleRefresh = () => refreshTasks(cluster);
  const handleChangeCluster = (event, newValue) => setCluster(newValue);
  const handleCreateTask = () => navigate("new-task");

  const dataTableColumns = createTableColumns(openDeleteDialog);

  return (
    <Layout>
      <ValidationAlert
        inputOpen={alertState.alertOpen}
        setInputClosed={closeAlert}
        severity={alertState.alertType}
        alertMessage={alertState.alertMsg}
      />

      {/* Display confirm delete dialog for task deletion */}
      <ConfirmDeleteDialog
        deleteDialog={deleteDialog}
        setDeleteDialog={setDeleteDialog}
        showAlert={showAlert}
        cluster={cluster}
        refreshTasks={handleRefresh}
      />

      <TopBarGridContainer>
        <Grid>
          {/* Title for active tasks */}
          <Title text="Active Tasks" sx={{ my: 5 }} />
        </Grid>

        <Grid size="grow">
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              mt: 5,
            }}
          >
            {/* Tabs to switch between clusters */}
            <Tabs
              value={cluster}
              onChange={handleChangeCluster}
              sx={{
                pr: 10,
                "& .MuiButtonBase-root.MuiTab-root": {
                  "&.Mui-selected": { color: "white" },
                },
                "& .MuiTabs-indicator": { backgroundColor: "#dbdbdb" },
              }}
            >
              <Tab label="Scraper Cluster" value={CLUSTERS.SCRAPER} />
              <Tab label="Auto4client Cluster" value={CLUSTERS.AUTO4CLIENT} />
            </Tabs>

            <TaskIconButton text="Refresh" icon={<RefreshIcon />} onClick={handleRefresh} color="" />
            <TaskIconButton text="Create new task" icon={<AddIcon />} onClick={handleCreateTask} color="" />
          </Box>
        </Grid>

        {/* DataTable for displaying active tasks */}
        <DataTable columns={dataTableColumns} dataRows={tasks} height="700px" />
      </TopBarGridContainer>
    </Layout>
  );
}

export default Tasks;
